import { Component } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { LucideCheck, LucideDynamicIcon, LucideMinus } from '@lucide/angular';
import { injectCheckboxState, NgpCheckbox } from 'ng-primitives/checkbox';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';

@Component({
  selector: 'joii-checkbox',
  hostDirectives: [
    {
      directive: NgpCheckbox,
      inputs: ['ngpCheckboxChecked:checked', 'ngpCheckboxIndeterminate:indeterminate', 'ngpCheckboxDisabled:disabled'],
      outputs: ['ngpCheckboxCheckedChange:checkedChange', 'ngpCheckboxIndeterminateChange:indeterminateChange'],
    },
  ],
  providers: [provideValueAccessor(CheckboxComponent)],
  imports: [LucideDynamicIcon],
  template: `
    @if (state().indeterminate()) {
      <svg [lucideIcon]="minusIcon" [size]="13" strokeWidth="3" />
    } @else if (state().checked()) {
      <svg [lucideIcon]="checkIcon" [size]="13" strokeWidth="3" />
    }
  `,
  styles: `
    @property --joii-checkbox-size {
      syntax: '<length>';
      inherits: false;
      initial-value: 24px;
    }

    :host {
      display: flex;
      width: var(--joii-checkbox-size);
      height: var(--joii-checkbox-size);
      aspect-ratio: 1 / 1;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      border-radius: var(--Vetai-Radius-1-5);
      border: 1px solid var(--Vetai-Form_controls--Border_Default);
      background-color: transparent;
      padding: 0;
      outline: none;
      flex: none;
      color: var(--Vetai-Texts--Base);
      font-size: var(--Vetai-T4);
      font-weight: var(--Vetai-Font-Weight-Regular);
      line-height: var(--Vetai-20);
      letter-spacing: var(--Vetai-8-16);
    }

    :host[data-hover],
    :host[data-focus-visible] {
      border-color: var(--Vetai-Form_controls--Border_Hover);
    }

    :host[data-checked],
    :host[data-indeterminate] {
      color: var(--Vetai-Positive_package--Default);
      border-color: var(--Vetai-Form_controls--Border_Active);
      background-color: var(--Vetai-Form_controls--Border_Active);
    }

    :host[disabled],
    :host[data-disabled] {
      background-color: var(--Vetai-Form_controls--Border_Default);
      border-color: var(--Vetai-Form_controls--Border_Default);
      color: var(--Vetai-Positive_package--Default);
      cursor: not-allowed;
    }
  `,
  host: {
    '(focusout)': 'onTouchedFn?.()',
  },
})
export class CheckboxComponent implements ControlValueAccessor {
  readonly checkIcon = LucideCheck;
  readonly minusIcon = LucideMinus;
  protected readonly state = injectCheckboxState();
  protected onChangeFn?: ChangeFn<boolean>;
  protected onTouchedFn?: TouchedFn;

  constructor() {
    this.state().checkedChange.subscribe((checked) => this.onChangeFn?.(checked));
  }

  writeValue(checked: boolean): void {
    // writeValue is the model -> view direction, so it must not emit: the
    // emitted change would come straight back through onChange as a view -> model
    // update and loop forever.
    this.state().setChecked(checked, { emit: false });
  }

  registerOnChange(fn: ChangeFn<boolean>): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.state().setDisabled(isDisabled);
  }
}
